import {findMatch, getPageNumber} from 'core/helpers/pagination';
import {usePageChange} from 'core/hooks/usePageChange';
import {PageHead} from 'core/modules/PageHead';
import {withBasicDataServerSide} from 'core/next/controllers';
import {BrandXPage} from 'modules/brands/pages/BrandXPage';
import {getBrandsProducts, getBrandX, useGetBrandsProductsQuery} from 'modules/brands/services';
import {NextPage} from 'next';
import {useRouter} from 'next/router';
import {PaginationProvider} from 'plugins/modules/pagination/provider';
import {get404Exception} from 'core/exceptions/NotFoundException';
import {getSeo} from 'modules/seo/services';
import {IBreadcrumb} from 'modules/seo/types';
import {useBrandPagination, useBrandProducts, useBrandSeo, useBrandX} from 'modules/brands/hooks/useBrandX';
import {extractBrandCodeFromPath} from 'modules/brands/helpers';
import {Typography} from 'components/Typography';
import {Box} from 'components/Grid';
import {STATUS_CODES} from 'core/constants';

interface IProps {
    page: number;
    breadcrumbs?: IBreadcrumb[];
}

const BrandPage: NextPage<{pageProps: IProps}> = ({pageProps: {page, breadcrumbs = []}}) => {
    const {asPath} = useRouter();
    const code = extractBrandCodeFromPath(asPath);

    const {brandSeo, brand} = useBrandX();

    const {metaTitle, metaDescription, robots} = useBrandSeo({
        brandSeo,
    });

    const {currentPage, onControlPageChange} = usePageChange({page});
    const {data: brandsData} = useGetBrandsProductsQuery({
        code,
        page: currentPage,
    });
    const {data, pagination} = brandsData || {};

    const {paginationType, onPageChange, onAccPageChange} = useBrandPagination({
        onControlPageChange,
    });

    const productsData = useBrandProducts({
        products: data?.items,
        paginationType,
        pageNumber: pagination?.pageNumber,
    });

    if (!brand) {
        return (
            <>
                <PageHead description={metaDescription} robots={robots} title={metaTitle} />
                <Box>
                    <Typography>Такого бренда не существует</Typography>
                </Box>
            </>
        );
    }

    return (
        <>
            <PageHead description={metaDescription} robots={robots} title={metaTitle} />
            <PaginationProvider
                count={pagination?.totalCount || 0}
                onAccPageChange={onAccPageChange}
                onPageClick={onPageChange}
                page={pagination?.pageNumber || 0}
                size={pagination?.pageSize || 0}
                type={paginationType}
            >
                <BrandXPage brandX={brand} products={productsData} breadcrumbs={breadcrumbs} />
            </PaginationProvider>
        </>
    );
};

export const getServerSideProps = withBasicDataServerSide(async (store, ctx) => {
    const url = ctx?.req.url;
    if (!url) {
        throw get404Exception();
    }

    const code = extractBrandCodeFromPath(url);

    if (!code) {
        throw get404Exception();
    }

    const seoResponse = await store.dispatch(
        getSeo.initiate({
            type: 'brand',
            filter: {
                code,
            },
        }),
    );

    // проверяем на ошибку 404 от seo запроса
    if (seoResponse?.error && 'status' in seoResponse.error && seoResponse.error.status === STATUS_CODES.NOT_FOUND) {
        throw get404Exception();
    }

    const pageStr = findMatch(url);
    const pageNumber = getPageNumber(pageStr);

    const brandResponse = await store.dispatch(getBrandX.initiate({code}));

    const {data: brandData} = brandResponse;

    if (!brandData?.data) {
        throw get404Exception();
    }

    store.dispatch(getBrandsProducts.initiate({code, page: Number(pageNumber)}));

    return {
        props: {
            page: pageNumber,
            breadcrumbs: seoResponse.data?.breadcrumbs ?? [],
        },
    };
});

export default BrandPage;
